import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import UserDropdown from '../UserDropdown';

const BrowseDrivers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [favoriteLoading, setFavoriteLoading] = useState({});
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    fetchDrivers();
  }, [sortBy]);

  useEffect(() => {
    filterDrivers();
  }, [searchTerm, drivers, capacityFilter]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('sort_by', sortBy);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/riders/drivers?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDrivers(response.data);
      setFilteredDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDrivers = () => {
    let filtered = [...drivers];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(driver =>
        `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by capacity
    if (capacityFilter !== 'all') {
      const [min, max] = capacityFilter.split('-').map(Number);
      filtered = filtered.filter(driver => {
        const capacity = driver.max_passenger_capacity || 0;
        if (max) {
          return capacity >= min && capacity <= max;
        } else {
          // "7+" case
          return capacity >= min;
        }
      });
    }

    setFilteredDrivers(filtered);
  };

  const toggleFavorite = async (driverId, isFavorited, e) => {
    if (e) e.stopPropagation();
    setFavoriteLoading(prev => ({ ...prev, [driverId]: true }));

    try {
      const token = localStorage.getItem('token');

      if (isFavorited) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/riders/favorites/${driverId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/riders/favorites/${driverId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setDrivers(prev => prev.map(driver =>
        driver.id === driverId
          ? { ...driver, is_favorited: !isFavorited }
          : driver
      ));

      // Also update selected driver if it's the one being toggled
      if (selectedDriver && selectedDriver.id === driverId) {
        setSelectedDriver(prev => ({ ...prev, is_favorited: !isFavorited }));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      if (error.response?.status === 400) {
        window.alert('Maximum 5 favorite drivers allowed. Please remove one first.');
      } else {
        window.alert('Failed to update favorite status');
      }
    } finally {
      setFavoriteLoading(prev => ({ ...prev, [driverId]: false }));
    }
  };

  const handleRequestRide = (driver) => {
    navigate('/request-ride', {
      state: { driver }
    });
  };

  const capacityOptions = [
    { value: 'all', label: 'All' },
    { value: '1-2', label: '1-2' },
    { value: '3-4', label: '3-4' },
    { value: '5-6', label: '5-6' },
    { value: '7-99', label: '7+' }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (user?.is_active === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-red-700 mb-3">Account Deactivated</h2>
          <p className="text-gray-600 mb-4">Your account has been deactivated. You cannot browse drivers or request rides.</p>
          <p className="text-sm text-gray-500 mb-6">Contact support@ridehowl.com for assistance.</p>
          <button onClick={() => navigate('/rider/dashboard')} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/rider/dashboard')} className="text-gray-600 hover:text-gray-900">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Browse Drivers</h1>
            </div>
            <UserDropdown userType="rider" />
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Name
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter driver name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passenger Capacity
              </label>
              <div className="flex gap-2 flex-wrap">
                {capacityOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setCapacityFilter(option.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                      capacityFilter === option.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="name">Name (A-Z)</option>
                <option value="total_rides">Most Rides</option>
              </select>
            </div>
          </div>
        </div>

        {/* Driver List - Simplified Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDrivers.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              No drivers found matching your criteria
            </div>
          ) : (
            filteredDrivers.map(driver => (
              <div
                key={driver.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition p-4 cursor-pointer relative"
                onClick={() => setSelectedDriver(driver)}
              >
                {/* Favorite Heart Button */}
                <button
                  onClick={(e) => toggleFavorite(driver.id, driver.is_favorited, e)}
                  disabled={favoriteLoading[driver.id]}
                  className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-gray-100 transition disabled:opacity-50 z-10"
                  title={driver.is_favorited ? "Remove from favorites" : "Add to favorites"}
                >
                  <svg
                    className={`w-5 h-5 ${driver.is_favorited ? 'text-red-500 fill-current' : 'text-gray-300 hover:text-red-300'}`}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill={driver.is_favorited ? "currentColor" : "none"}
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>

                <div className="flex items-center gap-3">
                  {/* Profile Picture or Silhouette */}
                  {driver.profile_picture_url ? (
                    <img
                      src={driver.profile_picture_url}
                      alt={`${driver.first_name} ${driver.last_name}`}
                      className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {driver.first_name} {driver.last_name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>{driver.max_passenger_capacity || '?'} passengers</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDriver(driver);
                    }}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 border border-blue-500 rounded-lg hover:bg-blue-50 transition"
                  >
                    View Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestRide(driver);
                    }}
                    className="flex-1 px-3 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Driver Details Modal */}
      {selectedDriver && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedDriver(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative p-6 pb-4">
              <button
                onClick={() => setSelectedDriver(null)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex items-center gap-4">
                {/* Large Profile Picture */}
                {selectedDriver.profile_picture_url ? (
                  <img
                    src={selectedDriver.profile_picture_url}
                    alt={`${selectedDriver.first_name} ${selectedDriver.last_name}`}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedDriver.first_name} {selectedDriver.last_name}
                    </h2>
                    <button
                      onClick={(e) => toggleFavorite(selectedDriver.id, selectedDriver.is_favorited, e)}
                      disabled={favoriteLoading[selectedDriver.id]}
                      className="p-1 hover:scale-110 transition-transform disabled:opacity-50"
                      title={selectedDriver.is_favorited ? "Remove from favorites" : "Add to favorites"}
                    >
                      <svg
                        className={`w-5 h-5 ${selectedDriver.is_favorited ? 'text-yellow-400 fill-current' : 'text-gray-300 hover:text-yellow-300'}`}
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill={selectedDriver.is_favorited ? "currentColor" : "none"}
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">{selectedDriver.total_rides || 0} completed rides</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 pb-6 space-y-4">
              {/* Passenger Capacity */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900">Passenger Capacity</p>
                  <p className="text-blue-700">{selectedDriver.max_passenger_capacity || 'Not specified'} passengers</p>
                </div>
              </div>

              {/* Vehicle Info */}
              {selectedDriver.vehicle_make && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Vehicle</p>
                    <p className="text-gray-700">
                      {selectedDriver.vehicle_year} {selectedDriver.vehicle_make} {selectedDriver.vehicle_model}
                      {selectedDriver.vehicle_color && ` (${selectedDriver.vehicle_color})`}
                    </p>
                  </div>
                </div>
              )}

              {/* Bio */}
              {selectedDriver.bio && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">About</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{selectedDriver.bio}</p>
                </div>
              )}

              {/* Request Ride Button */}
              <button
                onClick={() => handleRequestRide(selectedDriver)}
                className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition"
              >
                Request Ride
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseDrivers;
